import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { referralService } from '../services/referralService';
import { patientService } from '../services/patientService';

const STAGES = [
  { value: 'inquiry', label: 'Inquiry' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'intake', label: 'Intake' },
  { value: 'active', label: 'Active' },
  { value: 'closed_won', label: 'Closed — Won' },
  { value: 'closed_lost', label: 'Closed — Lost' },
];

const emptyForm = {
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  referralSource: '',
  referringProvider: '',
  reason: '',
  notes: '',
  stage: 'inquiry',
  priority: 'medium',
  patientId: '',
  nextFollowUpAt: '',
};

const Referrals = () => {
  const [referrals, setReferrals] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [stageFilter, setStageFilter] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const [refRes, patientRes] = await Promise.all([
        referralService.list(stageFilter ? { stage: stageFilter } : {}),
        patientService.getPatients({}),
      ]);
      setReferrals(refRes.data || []);
      setPatients(Array.isArray(patientRes) ? patientRes : patientRes.data || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load referrals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [stageFilter]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.contactName) {
      alert('Contact name is required');
      return;
    }
    try {
      setSaving(true);
      await referralService.create({
        ...form,
        patientId: form.patientId || undefined,
        nextFollowUpAt: form.nextFollowUpAt
          ? new Date(form.nextFollowUpAt).toISOString()
          : undefined,
      });
      setForm(emptyForm);
      setShowForm(false);
      await load();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to create referral');
    } finally {
      setSaving(false);
    }
  };

  const updateStage = async (id, stage) => {
    try {
      await referralService.update(id, { stage });
      await load();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to update stage');
    }
  };

  const patientLabel = (p) =>
    p?.fullName || [p?.firstName, p?.lastName].filter(Boolean).join(' ') || p?._id;

  return (
    <Layout>
      <div className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-poppins font-semibold text-primary">CRM / Referrals</h1>
              <p className="text-gray-500 mt-1">Track inquiries from first contact through intake.</p>
            </div>
            <button
              className="bg-primary text-white px-4 py-2 rounded-lg font-poppins"
              onClick={() => setShowForm((v) => !v)}
            >
              {showForm ? 'Cancel' : 'New Referral'}
            </button>
          </div>

          {showForm && (
            <form
              onSubmit={handleCreate}
              className="bg-white rounded-xl shadow p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              <label className="flex flex-col gap-1 text-sm">
                Contact name *
                <input name="contactName" value={form.contactName} onChange={handleChange} className="border rounded px-3 py-2" required />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Email
                <input name="contactEmail" type="email" value={form.contactEmail} onChange={handleChange} className="border rounded px-3 py-2" />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Phone
                <input name="contactPhone" value={form.contactPhone} onChange={handleChange} className="border rounded px-3 py-2" />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Source
                <input name="referralSource" value={form.referralSource} onChange={handleChange} placeholder="Website, MD referral…" className="border rounded px-3 py-2" />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Referring provider
                <input name="referringProvider" value={form.referringProvider} onChange={handleChange} className="border rounded px-3 py-2" />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Link patient (optional)
                <select name="patientId" value={form.patientId} onChange={handleChange} className="border rounded px-3 py-2">
                  <option value="">— None yet —</option>
                  {patients.map((p) => (
                    <option key={p._id} value={p._id}>{patientLabel(p)}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm md:col-span-2">
                Reason / presenting concern
                <textarea name="reason" value={form.reason} onChange={handleChange} rows={2} className="border rounded px-3 py-2" />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Priority
                <select name="priority" value={form.priority} onChange={handleChange} className="border rounded px-3 py-2">
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Next follow-up
                <input name="nextFollowUpAt" type="datetime-local" value={form.nextFollowUpAt} onChange={handleChange} className="border rounded px-3 py-2" />
              </label>
              <div className="md:col-span-2">
                <button type="submit" disabled={saving} className="bg-primary text-white px-5 py-2 rounded-lg">
                  {saving ? 'Saving…' : 'Create referral'}
                </button>
              </div>
            </form>
          )}

          <div className="mb-4 flex gap-2 flex-wrap items-center">
            <span className="text-sm text-gray-500">Filter:</span>
            <button
              className={`px-3 py-1 rounded text-sm ${!stageFilter ? 'bg-primary text-white' : 'bg-gray-100'}`}
              onClick={() => setStageFilter('')}
            >
              All
            </button>
            {STAGES.map((s) => (
              <button
                key={s.value}
                className={`px-3 py-1 rounded text-sm ${stageFilter === s.value ? 'bg-primary text-white' : 'bg-gray-100'}`}
                onClick={() => setStageFilter(s.value)}
              >
                {s.label}
              </button>
            ))}
          </div>

          {loading && <p>Loading…</p>}
          {error && <p className="text-red-600">{error}</p>}

          {!loading && !error && (
            <div className="bg-white rounded-xl shadow overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="p-3">Contact</th>
                    <th className="p-3">Source</th>
                    <th className="p-3">Stage</th>
                    <th className="p-3">Priority</th>
                    <th className="p-3">Patient</th>
                    <th className="p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-6 text-center text-gray-500">
                        No referrals yet. Add an inquiry to start the pipeline.
                      </td>
                    </tr>
                  )}
                  {referrals.map((r) => (
                    <tr key={r._id} className="border-b">
                      <td className="p-3">
                        <div className="font-medium">{r.contactName}</div>
                        <div className="text-gray-500 text-xs">
                          {[r.contactEmail, r.contactPhone].filter(Boolean).join(' · ')}
                        </div>
                        {r.reason && <div className="text-xs mt-1 text-gray-600">{r.reason}</div>}
                      </td>
                      <td className="p-3">{r.referralSource || '—'}</td>
                      <td className="p-3">
                        <select
                          value={r.stage}
                          onChange={(e) => updateStage(r._id, e.target.value)}
                          className="border rounded px-2 py-1"
                        >
                          {STAGES.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="p-3 capitalize">{r.priority}</td>
                      <td className="p-3">
                        {r.patientId
                          ? patientLabel(typeof r.patientId === 'object' ? r.patientId : { _id: r.patientId })
                          : '—'}
                      </td>
                      <td className="p-3">
                        <button
                          className="text-red-600 text-xs"
                          onClick={async () => {
                            if (!window.confirm('Delete this referral?')) return;
                            await referralService.remove(r._id);
                            await load();
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Referrals;
